// The tune's notation as an embeddable READ-ONLY pane (unification Phase 3,
// user rulings July 2026: no edit surface, no chrome — a templated Real Book
// VIEW of the score): the lead part engraved on the printed lead sheet's
// grid with the band playhead, nothing else. All editing — note entry,
// durations, undo/redo, MusicXML import, parts — lives in the Score STUDIO
// (/score, via the main nav).
//
// Read-only is not inert: the chord SYMBOLS play, exactly like the chart's
// diagram cards — press to hear the voicing the optimiser chose at that spot
// (the same one the diagram sheet draws), double-click/tap for the inspector.
//
// PAGE VIEW: with `pages` on, the engraved strip is dealt into side-by-side
// page cards, one windowed ScoreView per page. The pager itself is
// `useScorePages` — shared with Score Studio, so the reading view and the
// editor cannot drift on paper size or page breaks.

import { useMemo, type ReactNode } from 'react';
import { ScoreView } from '@/components/ScoreView';
import { useStore } from '@/state/store';
import { editionScore, scoreEditionDef } from '@/music/score/edition';
import { usePassageSelection } from '@/components/passageSelection';
import {
  buildScoreView,
  useAnalysisLane,
  useBandPlayheadBeat,
  usePlayableChordSymbols,
} from '@/components/scoreShared';
import type { BandArrangement } from '@/audio/useBandArrangement';
import { useNotationMetrics } from '@/components/useNotationMetrics';
import { scaleVars } from '@/state/displayScale';
import { SheetPageCards } from '@/components/PagedSheet';
import { PrintSheet } from '@/components/PrintSheet';
import { usePrintMode } from '@/components/printMode';
import { useScorePages } from '@/components/useScorePages';

export function NotationPane({
  band,
  pages = false,
  header,
}: {
  band: BandArrangement;
  /** Page view (the workspace's Pages toggle): side-by-side fitted pages. */
  pages?: boolean;
  /** The masthead — a plain sibling when scrolling; in page view it prints
   * ON page 1's paper and only page 1 pays for it (PagedSheet's rule). */
  header?: ReactNode;
}) {
  const concert = useStore((s) => s.score);
  // The reading edition (B♭, E♭, bass clef) is a VIEW of the concert score,
  // derived once here so key, symbols, melody and clef all move by one rule;
  // the band keeps playing from the concert truth.
  const scoreEdition = useStore((s) => s.scoreEdition);
  const score = useMemo(() => editionScore(concert, scoreEdition), [concert, scoreEdition]);
  const scoreScale = useStore((s) => s.scoreScale);
  const chordSymbolStyle = useStore((s) => s.chordSymbolStyle);

  // A templated VIEW of the score, not the score itself: the Real Book page
  // doesn't repeat what the transport already shows, so the tempo words
  // ("Presto (♩ = 200)") are omitted HERE ONLY — the model keeps them and
  // the Score studio still prints them.
  const showLyrics = useStore((s) => s.showLyrics);
  const showBarNumbers = useStore((s) => s.showBarNumbers);
  // The same view choices as Score Studio: one part, or the stacked full
  // score (`scoreViewPart` = -1) with part names in the margin.
  const scoreViewPart = useStore((s) => s.scoreViewPart);
  const view = useMemo(() => {
    // A chord-only chart has no staff to reclef, so the edition's clef is the
    // fallback the empty staff draws on (the C bass version of a chord chart
    // IS a bass-clef sheet).
    const projected = buildScoreView(
      score,
      scoreViewPart,
      showLyrics,
      scoreEditionDef(scoreEdition).clef ?? undefined,
    );
    return {
      ...projected,
      song: { ...projected.song, tempoText: undefined, tempoBpm: undefined },
    };
  }, [score, scoreEdition, scoreViewPart, showLyrics]);
  const song = view.song;
  const bars = useMemo(() => song.sections.flatMap((s) => s.bars), [song]);
  const printing = usePrintMode();
  // The flowing sheet is hidden on paper, so the pager stops measuring it.
  const pager = useScorePages(pages && !printing);
  // In page mode the surface ref rides the FIRST page card's inner div, so
  // systemWidth follows the PAGE the music actually spans; the surfaceKey
  // carries the mode so the metrics re-measure when the ref moves elements.
  const {
    surfaceRef,
    spaceSize,
    systemWidth,
    horizontalMarginSpaces,
    horizontalEndMarginSpaces,
    lineGeometry,
    baseSpaceSize,
  } = useNotationMetrics(
    scoreScale,
    song.key,
    `part:${scoreViewPart}:${pager.paged ? 'pages' : 'flow'}`,
  );
  // The printed lead sheet's grid: the Bars per line setting (four by
  // default). The lines are fitted to the page at the default size, the staff
  // drawing smaller so they fit and past a quarter smaller splitting in half,
  // and the reader's size scales the result (lines.ts scaledLineFit). Until
  // the page is measured, lines that do not fit split. Auto fills the width.
  const barsPerLine = useStore((s) => s.barsPerLine);
  const options = useMemo(
    () => ({
      spaceSize,
      systemWidth,
      clef: view.clef,
      chordSymbolStyle,
      ...(barsPerLine === 'auto'
        ? { maxBarsPerSystem: 4 }
        : {
            barsPerLine,
            lineFit: lineGeometry && { geometry: lineGeometry, baseSpaceSize },
          }),
    }),
    [spaceSize, systemWidth, view.clef, chordSymbolStyle, barsPerLine, lineGeometry, baseSpaceSize],
  );
  const playheadBeat = useBandPlayheadBeat(band, bars);
  const playableChords = usePlayableChordSymbols(band);
  const showAnalysis = useStore((s) => s.showAnalysis);
  const analysisLane = useAnalysisLane(song, showAnalysis);
  const { passageIdSet, onSelectPassage } = usePassageSelection();

  const scoreView = (systemWindow?: readonly [number, number], first = true) => (
    <ScoreView
      barNumbers={showBarNumbers}
      song={song}
      options={options}
      extraStaves={view.extraStaves}
      staffMeta={view.staffMeta}
      playheadBeat={playheadBeat}
      {...playableChords}
      {...analysisLane}
      selectedMeasureIds={passageIdSet}
      onSelectPassage={onSelectPassage}
      onTapMeasure={band.seekToBar}
      cursorMeasureId={band.startCursor?.measureId}
      horizontalMarginSpaces={horizontalMarginSpaces}
      horizontalEndMarginSpaces={horizontalEndMarginSpaces}
      systemWindow={systemWindow}
      onSystemSpans={first ? pager.onSystemSpans : undefined}
    />
  );

  // One block per engraved SYSTEM: the printer packs as many as fit onto each
  // page and breaks between them, so a system is never cut in half and the
  // masthead is never left on a page of its own.
  const printSheet = printing ? (
    <PrintSheet
      header={header}
      count={pager.systemCount}
      render={(systemWindow) => (
        <div className="score-surface print-score-surface">{scoreView(systemWindow, false)}</div>
      )}
    />
  ) : null;

  if (!pager.paged) {
    return (
      <div
        ref={pager.probeRef}
        className="notation-pane"
        data-testid="notation-pane"
        data-score-scale=""
        style={scaleVars({ score: scoreScale })}
      >
        {header}
        <div
          ref={surfaceRef}
          className="score-surface"
          style={{ overflowX: 'auto', padding: '0.5rem 0' }}
        >
          {scoreView()}
        </div>
        {printSheet}
      </div>
    );
  }

  return (
    <div
      ref={pager.probeRef}
      className="notation-pane"
      data-testid="notation-pane"
      data-score-scale=""
      style={scaleVars({ score: scoreScale })}
    >
      <div ref={pager.containerRef} className="sheet-pages">
        <SheetPageCards
          gridRef={pager.gridRef}
          columns={pager.columns}
          pageWidth={pager.pageWidth}
          minHeight={pager.minHeight}
          windows={pager.windows}
          render={(win, i) => (
            <>
              {/* Title and composer print ON page 1, as an engraved score
                  does — later pages open with music at the top. */}
              {i === 0 && header && <div className="sheet-page-header">{header}</div>}
              <div
                ref={i === 0 ? surfaceRef : undefined}
                className="score-surface score-page-surface"
                style={{ padding: '0.5rem 0' }}
              >
                {scoreView(win, i === 0)}
              </div>
            </>
          )}
        />
      </div>
      {printSheet}
    </div>
  );
}
